import { Box, Divider } from "@mui/joy";
import { useForm, FormProvider } from "react-hook-form";
import { useRef, useState, useEffect } from "react";

import { buildEmptyMeasurements } from "../../constants/roofMeasurements";
import {
    InspectionPhotos,
    RoofMeasurements,
    ShingleAndRoofing,
    UnderlaymentAndShield,
    Ventilation,
    Penetrations,
    DripEdgeFlashingValley,
    Chimneys,
    Skylights,
    Equipment,
    GuttersAndDownspouts,
    AncillaryAndTrim,
    Documents,
    InitialInspectionSkeleton,
    AutoSaveStatusIndicator,
} from "./components";
import { TaskActionComponent, TaskHeader } from "../Tasks/components";
import { useSync, useInit, useAutoSave } from "./hooks";
import { useNotification } from "../../hooks";

const defaultValues = {
    Measurements: buildEmptyMeasurements(),
    ShingleType: "",
    RoofColor: "",
    TearOff: "",
    UnderlaymentType: "",
    IceWaterBarrierSQ: "",
    IceWaterBarrierAction: "",
    Starter: "",
    Valley: "",
    RidgeCapProfile: "",
    Stories: "",
    LowSlopeSQ: "",
    HighRoofSQ: "",
    RoofPitch: [{ Name: "", Pitch: "", Squares: "" }],
    VentilationType: "",
    BoxVentQty: "",
    BoxVentAction: "",
    TurbineVentQty: "",
    TurbineVentAction: "",
    VentilationPainted: "",
    BathVentSize: "",
    BathVentQty: "",
    BathVentAction: "",
    DryerVentSize: "",
    DryerVentQty: "",
    DryerVentAction: "",
    FurnaceVents: [{ Size: "", Qty: "", Action: "" }],
    PlumbingJacksQty: "",
    PlumbingJacksAction: "",
    SplitBootsQty: "",
    SplitBootsAction: "",
    EaveDripEdge: "",
    RakeDripEdge: "",
    ValleyType: "",
    StepFlashingQty: "",
    StepFlashingAction: "",
    CounterFlashingQty: "",
    CounterFlashingAction: "",
    HeadWallMetalQty: "",
    HeadWallMetalAction: "",
    KickoutDiverterQty: "",
    KickoutDiverterAction: "",
    Chimneys: [
        { Type: "Brick", Size: 'Average (32"x36")', Color: "", Count: 1 },
    ],
    Skylights: [
        {
            Type: "None",
            Size: "None",
            FlashingKit: "N/A",
            Count: 1,
            Notes: "",
        },
    ],
    EvaporativeCooler: "",
    GutterSize: "",
    GutterColor: "",
    GutterLengthQty: "",
    GutterLengthAction: "",
    DownspoutSize: "",
    DownspoutColor: "",
    DownspoutLengthQty: "",
    DownspoutLengthAction: "",
    DirectionalElbowStyleAQty: "",
    DirectionalElbowStyleBQty: "",
    OffsetQty: "",
    GutterNotes: "",
    GutterGuardType: "",
    PaintGutterQty: "",
    PaintGutterAction: "",
    CorniceReturnsQty: "",
    CorniceReturnsAction: "",
    CorniceStripQty: "",
    CorniceStripAction: "",
    FasciaBoardQty: "",
    FasciaBoardAction: "",
    SoffitInches: "",
    GutterApronQty: "",
    GutterApronAction: "",
    SheathingQty: "",
    SheathingAction: "",
};

function FormSections({
    taskId,
    projectId,
}: {
    taskId: string;
    projectId: string;
}) {
    return (
        <Box>
            <InspectionPhotos taskId={taskId} projectId={projectId} />
            <Divider sx={{ my: 3 }} />
            <ShingleAndRoofing />
            <Divider sx={{ my: 3 }} />
            <UnderlaymentAndShield />
            <Divider sx={{ my: 3 }} />
            <Ventilation />
            <Divider sx={{ my: 3 }} />
            <Penetrations />
            <Divider sx={{ my: 3 }} />
            <DripEdgeFlashingValley />
            <Divider sx={{ my: 3 }} />
            <Chimneys />
            <Divider sx={{ my: 3 }} />
            <Skylights />
            <Divider sx={{ my: 3 }} />
            <GuttersAndDownspouts />
            <Divider sx={{ my: 3 }} />
            <Equipment />
            <Divider sx={{ my: 3 }} />
            <AncillaryAndTrim />
            <Divider sx={{ my: 3 }} />
            <RoofMeasurements />
            <Divider sx={{ my: 3 }} />
            <Documents />
        </Box>
    );
}

export default function InitialInspection({ activeTask, onComplete }: any) {
    const { Id, ProjectId } = activeTask || {};
    const { showError } = useNotification();

    const methods = useForm({
        defaultValues: defaultValues,
    });

    const { syncEngine, syncing } = useSync();
    const skipNextAutoSaveRef = useRef(false);
    const [autoSaveReady, setAutoSaveReady] = useState(false);

    const { baselineAttributes, setBaselineAttributes, loading } = useInit({
        projectId: ProjectId,
        onSuccess: (flatData) => {
            methods.reset({ ...defaultValues, ...flatData });
            skipNextAutoSaveRef.current = true;
            setAutoSaveReady(true);
        },
    });

    const { saveStatus, flushSave } = useAutoSave({
        enabled: autoSaveReady && !loading,
        projectId: ProjectId,
        watch: methods.watch,
        getValues: methods.getValues,
        baselineAttributes,
        setBaselineAttributes,
        syncEngine,
        skipNextSaveRef: skipNextAutoSaveRef,
        onError: (message) => showError(message),
    });

    useEffect(() => {
        if (!loading && ProjectId && !autoSaveReady) {
            skipNextAutoSaveRef.current = true;
            setAutoSaveReady(true);
        }
    }, [loading, ProjectId, autoSaveReady]);

    const handleComplete = async () => {
        const result = await flushSave();
        if (!result?.success) {
            return;
        }

        if (onComplete) {
            onComplete();
        }
    };

    const isSaving = syncing || saveStatus === "saving";

    return (
        <FormProvider {...methods}>
            <Box sx={{ position: "relative", minHeight: 400 }}>
                {loading ? (
                    <Box
                        sx={{
                            animation: "fadeIn 0.3s ease-in-out",
                            px: { xs: 2, sm: 3 },
                        }}
                    >
                        <InitialInspectionSkeleton />
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            mt: 1,
                            px: { xs: 2, sm: 3 },
                            animation: "fadeIn 0.5s ease-in-out",
                            "@keyframes fadeIn": {
                                "0%": { opacity: 0 },
                                "100%": { opacity: 1 },
                            },
                        }}
                    >
                        <TaskHeader activeTask={activeTask} />
                        <AutoSaveStatusIndicator status={saveStatus} />
                        <Divider />
                        <FormSections
                            taskId={Id || ""}
                            projectId={ProjectId || ""}
                        />

                        <Box mt={4}>
                            <Divider />
                            <TaskActionComponent
                                taskName={activeTask?.Name || ""}
                                isNextEligible={true}
                                actionProgress={{ loading: isSaving }}
                                onComplete={handleComplete}
                                taskId={Id || ""}
                            />
                        </Box>
                    </Box>
                )}
            </Box>
        </FormProvider>
    );
}
